import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { App, Button, Card, Descriptions, Flex, List, Space, Spin, Tabs, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import AiAnalysisBadge from '../../components/AiAnalysisBadge';
import RiskBadge from '../../components/RiskBadge';
import ScoreBadge from '../../components/ScoreBadge';
import AudioPlayer from '../../components/AudioPlayer';
import EmptyState from '../../components/EmptyState';
import ReviewPanel from './ReviewPanel';
import { callsApi } from '../../api/calls.api';
import { toApiError } from '../../api/client';

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const num = (v: number | null) => (v == null ? '—' : v.toFixed(1));

export default function CallDetail() {
  const { callId } = useParams();
  const id = Number(callId);
  const { message } = App.useApp();
  const [audioSrc, setAudioSrc] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['call', id], queryFn: () => callsApi.get(id), enabled: Number.isFinite(id) });

  if (q.isLoading) return <Flex justify="center" style={{ padding: 48 }}><Spin size="large" /></Flex>;
  if (q.isError || !q.data) return <EmptyState description="تماس یافت نشد یا در دسترس شما نیست." />;

  const call = q.data;
  const a = call.analysis;
  const e = call.evaluation;

  const loadAudio = async () => {
    try {
      const { url } = await callsApi.audioUrl(id);
      setAudioSrc(url);
    } catch (err) {
      message.error(toApiError(err).message);
    }
  };

  const summaryTab = (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="شناسه تماس">{call.externalCallId}</Descriptions.Item>
        <Descriptions.Item label="اپراتور">{call.operatorName}</Descriptions.Item>
        <Descriptions.Item label="زمان">{dayjs(call.timestamp).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
        <Descriptions.Item label="مدت (ثانیه)">{call.durationSeconds}</Descriptions.Item>
        <Descriptions.Item label="کانال">{call.channel || '—'}</Descriptions.Item>
        <Descriptions.Item label="نوع تماس">{call.callType || '—'}</Descriptions.Item>
        <Descriptions.Item label="وضعیت پردازش">{call.processingStatus}{call.errorReason ? ` (${call.errorReason})` : ''}</Descriptions.Item>
        <Descriptions.Item label={<Space size={4}>ریسک <AiAnalysisBadge compact /></Space>}><RiskBadge level={call.riskLevel} /></Descriptions.Item>
        <Descriptions.Item label={<Space size={4}>امتیاز کیفیت <AiAnalysisBadge compact /></Space>}><ScoreBadge score={e?.overallQualityScore ?? null} /></Descriptions.Item>
        <Descriptions.Item label="بازبینی">{call.humanReviewRequired ? call.humanReviewStatus : 'لازم نیست'}</Descriptions.Item>
        <Descriptions.Item label="پرچم‌ها">
          <Space>
            {call.partialScore && <Tag>امتیاز جزئی</Tag>}
            {call.followUpFlag && <Tag color="blue">پیگیری</Tag>}
            {call.trainingCaseFlag && <Tag color="purple">مورد آموزشی</Tag>}
            {!call.partialScore && !call.followUpFlag && !call.trainingCaseFlag && '—'}
          </Space>
        </Descriptions.Item>
      </Descriptions>

      <Card size="small" title="پخش صوت">
        {audioSrc ? <AudioPlayer src={audioSrc} /> : (
          <Button onClick={loadAudio} disabled={!call.hasAudio}>
            {call.hasAudio ? 'بارگذاری و پخش صوت' : 'صوتی موجود نیست'}
          </Button>
        )}
      </Card>
    </Space>
  );

  const transcriptTab = call.transcript ? (
    <Space direction="vertical" style={{ display: 'flex' }}>
      <AiAnalysisBadge />
      {call.transcript.diarizationFailed && <Tag color="warning">تفکیک گوینده انجام نشد</Tag>}
      {call.transcript.segments.length > 0 ? (
        <List
          size="small"
          dataSource={call.transcript.segments}
          renderItem={(s) => (
            <List.Item>
              {!call.transcript!.diarizationFailed && <Tag color={s.speaker === 0 ? 'geekblue' : 'green'}>{s.speaker === 0 ? 'اپراتور' : 'مشتری'}</Tag>}
              <Typography.Text type="secondary" style={{ marginInline: 8 }}>{s.timestamp.toFixed(1)}s</Typography.Text>
              {s.text}
            </List.Item>
          )}
        />
      ) : (
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{call.transcript.fullText}</Typography.Paragraph>
      )}
    </Space>
  ) : <EmptyState description="رونوشتی موجود نیست." />;

  const analysisTab = a ? (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <AiAnalysisBadge />
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="خلاصه">{a.summary ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="موضوع">{a.topic ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="کلیدواژه‌ها">{a.keywords.length ? <Space wrap>{a.keywords.map((k) => <Tag key={k}>{k}</Tag>)}</Space> : '—'}</Descriptions.Item>
        <Descriptions.Item label="مشکل مشتری">{a.customerIssue ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="نتیجه تماس">{a.callOutcome ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="وضعیت رفع">{a.resolutionStatus ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="نارضایتی">{pct(a.dissatisfactionScore)}</Descriptions.Item>
        <Descriptions.Item label="خشم/تنش">{pct(a.angerTensionScore)}</Descriptions.Item>
        <Descriptions.Item label="احتمال شکایت">{pct(a.complaintLikelihoodScore)}</Descriptions.Item>
      </Descriptions>
    </Space>
  ) : <EmptyState description="تحلیلی موجود نیست." />;

  const evaluationTab = e ? (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <AiAnalysisBadge />
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="لحن">{num(e.toneScore)}</Descriptions.Item>
        <Descriptions.Item label="ادب">{num(e.politenessScore)}</Descriptions.Item>
        <Descriptions.Item label="کیفیت ارتباط">{num(e.communicationQualityScore)}</Descriptions.Item>
        <Descriptions.Item label="کیفیت پاسخ">{num(e.responseQualityScore)}</Descriptions.Item>
        <Descriptions.Item label="انطباق اسکریپت">{e.scriptComplianceScore == null ? 'در انتظار محتوای کارفرما (۴.۳)' : num(e.scriptComplianceScore)}</Descriptions.Item>
        <Descriptions.Item label="تطبیق پایگاه دانش">{e.knowledgeBaseMatchResult ?? 'در انتظار محتوای کارفرما (۴.۴)'}</Descriptions.Item>
        <Descriptions.Item label="امتیاز کلی" span={2}><ScoreBadge score={e.overallQualityScore} /></Descriptions.Item>
      </Descriptions>
      {e.scoringReasons.length > 0 && (
        <Card size="small" title="دلایل امتیاز">
          <List size="small" dataSource={e.scoringReasons} renderItem={(x) => <List.Item>{x}</List.Item>} />
        </Card>
      )}
      {e.flaggedResponses.length > 0 && (
        <Card size="small" title="پاسخ‌های نشان‌دار">
          <List size="small" dataSource={e.flaggedResponses} renderItem={(f) => (
            <List.Item><Tag color="red">{f.severity}</Tag> <b>{f.reason}:</b> {f.responseText}</List.Item>
          )} />
        </Card>
      )}
    </Space>
  ) : <EmptyState description="ارزیابی‌ای موجود نیست." />;

  const reviewTab = <ReviewPanel callId={id} call={call} />;

  return (
    <Card title={<Typography.Title level={4} style={{ margin: 0 }}>جزئیات تماس: {call.externalCallId}</Typography.Title>}>
      <Tabs
        items={[
          { key: 'summary', label: 'خلاصه', children: summaryTab },
          { key: 'transcript', label: 'رونوشت', children: transcriptTab },
          { key: 'analysis', label: 'تحلیل', children: analysisTab },
          { key: 'evaluation', label: 'ارزیابی', children: evaluationTab },
          { key: 'review', label: 'بازبینی انسانی', children: reviewTab },
        ]}
      />
    </Card>
  );
}
